/**
 * Browser-specific Blob/File I/O utilities.
 */

/**
 * Convert a Blob or File to a Uint8Array.
 */
export async function blobToUint8Array(blob: Blob): Promise<Uint8Array> {
  const buffer = await blob.arrayBuffer();
  return new Uint8Array(buffer);
}

/**
 * Convert a Uint8Array to a Blob.
 */
export function uint8ArrayToBlob(data: Uint8Array, mimeType = 'application/octet-stream'): Blob {
  return new Blob([data.slice().buffer as ArrayBuffer], { type: mimeType });
}

/**
 * Convert a Uint8Array to an Object URL (blob: URL).
 * Remember to call URL.revokeObjectURL when done.
 */
export function uint8ArrayToObjectURL(data: Uint8Array, mimeType = 'application/octet-stream'): string {
  const blob = uint8ArrayToBlob(data, mimeType);
  return URL.createObjectURL(blob);
}

/**
 * Trigger a browser download from a Uint8Array.
 */
export function downloadUint8Array(data: Uint8Array, filename: string, mimeType = 'application/octet-stream'): void {
  const url = uint8ArrayToObjectURL(data, mimeType);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  // Clean up after a short delay to ensure download starts
  setTimeout(() => {
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }, 100);
}

/**
 * Read a File input element's selected file as a Uint8Array.
 */
export async function readFileInput(input: HTMLInputElement): Promise<{ name: string; data: Uint8Array } | null> {
  const file = input.files?.[0];
  if (!file) return null;
  const data = await blobToUint8Array(file);
  return { name: file.name, data };
}

/**
 * Common MIME types for media files.
 */
export const MIME_TYPES: Record<string, string> = {
  mp4: 'video/mp4',
  webm: 'video/webm',
  mkv: 'video/x-matroska',
  avi: 'video/x-msvideo',
  mov: 'video/quicktime',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  flac: 'audio/flac',
  aac: 'audio/aac',
  webp: 'image/webp',
  png: 'image/png',
  jpg: 'image/jpeg',
  gif: 'image/gif',
};

/**
 * Guess the MIME type from a file extension.
 */
export function guessMimeType(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  return MIME_TYPES[ext] ?? 'application/octet-stream';
}
